/**
 * Global API Error Handler
 * Catches any API errors and displays error page instead of XML/raw responses
 */

export function showErrorPage(message: string = 'Something went wrong'): void {
  // Create an error page overlay
  const errorHTML = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Error</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          background: linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%);
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
        }
        .error-container { text-align: center; max-width: 600px; }
        .error-icon { font-size: 80px; margin-bottom: 20px; }
        h1 { font-size: 48px; font-weight: 700; margin-bottom: 10px; color: #1a1a1a; }
        .error-message { font-size: 18px; color: #666; margin-bottom: 30px; }
        .error-button {
          padding: 12px 32px;
          font-size: 14px;
          font-weight: 600;
          background: #1a1a1a;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          text-transform: uppercase;
        }
        .error-button:hover { background: #333; }
      </style>
    </head>
    <body>
      <div class="error-container">
        <div class="error-icon">⚠️</div>
        <h1>Oops!</h1>
        <p class="error-message">${message}</p>
        <button class="error-button" onclick="location.href='https://micahlynise.com'">Go Home</button>
      </div>
    </body>
    </html>
  `;

  // Replace entire page with error HTML
  document.open();
  document.write(errorHTML);
  document.close();
}

/**
 * Intercept all fetch responses globally
 */
export function initializeGlobalFetchInterceptor(): void {
  const originalFetch = window.fetch.bind(window);

  (window as any).fetch = function(...args: Parameters<typeof fetch>): ReturnType<typeof fetch> {
    return originalFetch(...args)
      .then((response: Response) => {
        const contentType = response.headers.get('content-type') || '';
        const url = args[0] && typeof args[0] === 'string' ? args[0] : '';
        const isApiCall = url.includes('api.micahlynise.com') || url.includes('api.');
        
        // If we get HTML/XML from API endpoint, it's an error from API Gateway
        if (isApiCall && (contentType.includes('text/html') || contentType.includes('application/xml'))) {
          showErrorPage('Service temporarily unavailable. Please try again.');
          return response;
        }

        // If API returns 404 with HTML/XML content, show error page
        if (response.status === 404 && contentType.includes('text/html')) {
          showErrorPage('Page not found. Please go back and try again.');
          return response;
        }

        // If API returns 5xx error, show error page
        if (response.status >= 500) {
          showErrorPage('Something went wrong. Please try again.');
        }

        return response;
      })
      .catch((error: Error) => {
        console.error('Fetch error:', error);
        showErrorPage('Connection error. Please check your internet and try again.');
        throw error;
      });
  };
}
